import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, X, Trash2, ShieldCheck, User as UserIcon, Copy, Check, Pencil } from "lucide-react";
import { toast } from "sonner";

// Shared cache — filled once per page load. Avoids re-fetching between modals.
let _ridersCache = null;
let _horsesCache = null;
let _associationsCache = null;

async function loadRiders() {
  if (_ridersCache) return _ridersCache;
  const r = await api.get("/riders");
  _ridersCache = (r.data.riders || []).map((x) => x.name).filter(Boolean);
  return _ridersCache;
}
async function loadHorses() {
  if (_horsesCache) return _horsesCache;
  const r = await api.get("/horses");
  _horsesCache = (r.data.horses || []).map((x) => x.name).filter(Boolean);
  return _horsesCache;
}
async function loadAssociations() {
  if (_associationsCache) return _associationsCache;
  const r = await api.get("/associations");
  _associationsCache = (r.data.associations || []).map((x) => x.name).filter(Boolean);
  return _associationsCache;
}

/**
 * Autocomplete picker with chips.
 * Shows a native browser dropdown of existing options as you type,
 * but still lets you type a brand-new name and add it.
 */
function ChipPicker({ label, options, values, onChange, placeholder, listId, testid }) {
  const [input, setInput] = useState("");
  const commit = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) { setInput(""); return; }
    onChange([...values, v]);
    setInput("");
  };
  return (
    <div>
      <div className="label-tiny mb-1">{label}</div>
      <div className="flex gap-2">
        <input
          list={listId}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(input); } }}
          onBlur={() => { if (input.trim()) commit(input); }}
          placeholder={placeholder}
          className="field-input flex-1"
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        <button type="button" onClick={() => commit(input)} className="btn-secondary !py-2 text-xs whitespace-nowrap">Add</button>
      </div>
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testid}-chips`}>
          {values.map((v, i) => (
            <span key={i} className="inline-flex items-center gap-1 border border-stone-700 bg-stone-900 px-2 py-0.5 text-xs">
              {v}
              <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))} className="text-stone-500 hover:text-rose-400">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [lastCreated, setLastCreated] = useState(null); // {email, password}

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/users");
      setUsers(r.data.users || []);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const del = async (id, email) => {
    if (!window.confirm(`Delete user ${email}? Their gallery access is revoked.`)) return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("User deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const reset = async (id, email) => {
    const pwd = window.prompt(`New password for ${email}?`);
    if (!pwd) return;
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.patch(`/auth/users/${id}`, { password: pwd });
      toast.success("Password reset");
      setLastCreated({ email, password: pwd });
    } catch (e) {
      toast.error("Failed to reset");
    }
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1100px]" data-testid="users-page">
      <div className="flex items-end justify-between mb-8 gap-6">
        <div className="border-l-4 border-orange-600 pl-5">
          <div className="label-tiny mb-2">User Management</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Users</h1>
          <p className="text-stone-400 mt-3 max-w-xl">Create logins for your clients. They'll only see galleries you assign to them.</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex items-center gap-2" data-testid="create-user-btn">
          <Plus size={14} /> New User
        </button>
      </div>

      {lastCreated && <CredentialBanner creds={lastCreated} onDismiss={() => setLastCreated(null)} />}

      {loading ? (
        <div className="text-stone-500 py-12 text-center">Loading…</div>
      ) : users.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-16 text-center">
          <UserIcon size={32} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">No users yet.</p>
        </div>
      ) : (
        <div className="border border-stone-800" data-testid="users-table">
          <div className="grid grid-cols-[2fr_2fr_100px_120px_auto] gap-3 px-4 py-2 border-b border-stone-800 text-[10px] uppercase tracking-widest text-stone-500">
            <div>Name</div><div>Email</div><div>Role</div><div>Created</div><div></div>
          </div>
          {users.map((u) => (
            <div key={u.id} className="grid grid-cols-[2fr_2fr_100px_120px_auto] gap-3 px-4 py-3 border-b border-stone-900 items-center" data-testid={`user-row-${u.id}`}>
              <div className="truncate inline-flex items-center gap-2">
                {u.role === "admin" ? <ShieldCheck size={13} className="text-orange-500" /> : <UserIcon size={13} className="text-stone-500" />}
                {u.name || <span className="text-stone-600 italic">unnamed</span>}
              </div>
              <div className="text-sm text-stone-400 truncate">{u.email}</div>
              <div className="text-xs uppercase tracking-widest text-orange-500/80">{u.role}</div>
              <div className="text-xs text-stone-500">{(u.created_at || "").slice(0, 10)}</div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setEditUser(u)} className="btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1" data-testid={`user-edit-${u.id}`}>
                  <Pencil size={11} /> Edit
                </button>
                <button onClick={() => reset(u.id, u.email)} className="btn-secondary !py-1 !px-2 text-[10px]" data-testid={`user-reset-${u.id}`}>Reset Password</button>
                <button onClick={() => del(u.id, u.email)} className="btn-secondary !py-1 !px-2 hover:!text-rose-500" data-testid={`user-delete-${u.id}`}>
                  <Trash2 size={11} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={(creds) => { setShowCreate(false); setLastCreated(creds); refresh(); }}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); refresh(); }}
        />
      )}
    </div>
  );
}

function CredentialBanner({ creds, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const text = `Email: ${creds.email}\nPassword: ${creds.password}`;
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="border border-orange-700 bg-orange-600/10 p-4 mb-6 flex items-start gap-4" data-testid="credential-banner">
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-widest text-orange-400 mb-1">Save these credentials — they're shown once</div>
        <div className="font-mono-tc text-sm break-all">{creds.email} · {creds.password}</div>
      </div>
      <button onClick={copy} className="btn-secondary inline-flex items-center gap-1 text-xs" data-testid="copy-creds">
        {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Copied" : "Copy"}
      </button>
      <button onClick={onDismiss} className="text-stone-500 hover:text-stone-200"><X size={14} /></button>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("client");
  const [phone, setPhone] = useState("");
  const [linkedRiders, setLinkedRiders] = useState([]);
  const [linkedHorses, setLinkedHorses] = useState([]);
  const [linkedAssociations, setLinkedAssociations] = useState([]);
  const [riderOptions, setRiderOptions] = useState([]);
  const [horseOptions, setHorseOptions] = useState([]);
  const [associationOptions, setAssociationOptions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadRiders().then(setRiderOptions).catch(() => {});
    loadHorses().then(setHorseOptions).catch(() => {});
    loadAssociations().then(setAssociationOptions).catch(() => {});
  }, []);

  const generate = () => {
    const w = ["barrel", "lasso", "spur", "rodeo", "dusty", "saddle", "rope"];
    const pwd = `${w[Math.floor(Math.random() * w.length)]}${Math.floor(1000 + Math.random() * 9000)}`;
    setPassword(pwd);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/users", {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        password,
        role,
        phone_number: phone.trim(),
        linked_rider_names: linkedRiders,
        linked_horse_names: linkedHorses,
        linked_association_names: linkedAssociations,
      });
      toast.success("User created");
      onCreated({ email: email.trim().toLowerCase(), password });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || `Failed to create user${err?.message ? ` — ${err.message}` : ""}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-stone-950 border border-stone-800 w-full max-w-md p-6 my-auto" onClick={(e) => e.stopPropagation()} data-testid="create-user-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display uppercase text-2xl font-bold">New User</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <div className="label-tiny mb-1">Name</div>
            <input value={name} onChange={(e) => setName(e.target.value)} className="field-input w-full" data-testid="new-user-name" />
          </div>
          <div>
            <div className="label-tiny mb-1">Email *</div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="field-input w-full" data-testid="new-user-email" />
          </div>
          <div>
            <div className="label-tiny mb-1">Phone number (optional)</div>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. +1 403 555 0100" className="field-input w-full" data-testid="new-user-phone" />
          </div>
          <div>
            <div className="label-tiny mb-1">Password * (min 6 chars)</div>
            <div className="flex gap-2">
              <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="field-input flex-1" data-testid="new-user-password" />
              <button type="button" onClick={generate} className="btn-secondary !py-2 text-xs whitespace-nowrap" data-testid="generate-pwd">Generate</button>
            </div>
          </div>
          <div>
            <div className="label-tiny mb-1">Role</div>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="field-input w-full" data-testid="new-user-role">
              <option value="client">Client (portal-only access)</option>
              <option value="photographer">Photographer (marketplace seller)</option>
              <option value="videographer">Videographer (marketplace seller)</option>
              <option value="admin">Admin (full studio access)</option>
            </select>
          </div>

          {role === "client" && (
            <>
              <ChipPicker
                label="Linked rider names"
                options={riderOptions}
                values={linkedRiders}
                onChange={setLinkedRiders}
                placeholder="Type to search — or add a new one"
                listId="create-user-rider-list"
                testid="new-user-rider"
              />
              <div className="text-[10px] text-stone-500 -mt-2">Client sees any clip whose rider name matches (case-insensitive).</div>

              <ChipPicker
                label="Linked horse names"
                options={horseOptions}
                values={linkedHorses}
                onChange={setLinkedHorses}
                placeholder="Type to search — or add a new one"
                listId="create-user-horse-list"
                testid="new-user-horse"
              />
              <div className="text-[10px] text-stone-500 -mt-2">Or matching horse. Both filters use OR — either match shows the clip.</div>
            </>
          )}

          <ChipPicker
            label="Associations they administer"
            options={associationOptions}
            values={linkedAssociations}
            onChange={setLinkedAssociations}
            placeholder="e.g. CCA, WPRA — press Enter"
            listId="create-user-assoc-list"
            testid="new-user-association"
          />
          <div className="text-[10px] text-stone-500 -mt-2">Shown as "My Associations" on their account. Multiple allowed.</div>

          <button type="submit" disabled={busy} className="btn-primary w-full mt-2" data-testid="new-user-submit">
            {busy ? "Creating…" : "Create User"}
          </button>
        </form>
      </div>
    </div>
  );
}


function EditUserModal({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone_number || "");
  const [role, setRole] = useState(user.role || "client");
  const [linkedRiders, setLinkedRiders] = useState(user.linked_rider_names || []);
  const [linkedHorses, setLinkedHorses] = useState(user.linked_horse_names || []);
  const [linkedAssociations, setLinkedAssociations] = useState(user.linked_association_names || []);
  const [riderOptions, setRiderOptions] = useState([]);
  const [horseOptions, setHorseOptions] = useState([]);
  const [associationOptions, setAssociationOptions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadRiders().then(setRiderOptions).catch(() => {});
    loadHorses().then(setHorseOptions).catch(() => {});
    loadAssociations().then(setAssociationOptions).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/auth/users/${user.id}`, {
        name: name.trim(),
        role,
        phone_number: phone.trim(),
        linked_rider_names: linkedRiders,
        linked_horse_names: linkedHorses,
        linked_association_names: linkedAssociations,
      });
      toast.success("User updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-stone-950 border border-stone-800 w-full max-w-md p-6 my-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display uppercase text-2xl font-bold">Edit User</h2>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-200"><X size={18} /></button>
        </div>
        <div className="text-xs text-stone-500 mb-4 font-mono-tc break-all">{user.email}</div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <div className="label-tiny mb-1">Name</div>
            <input value={name} onChange={(e) => setName(e.target.value)} className="field-input w-full" data-testid="edit-user-name" />
          </div>
          <div>
            <div className="label-tiny mb-1">Phone number</div>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. +1 403 555 0100" className="field-input w-full" data-testid="edit-user-phone" />
          </div>
          <div>
            <div className="label-tiny mb-1">Role</div>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="field-input w-full" data-testid="edit-user-role">
              <option value="client">Client (portal-only access)</option>
              <option value="photographer">Photographer (marketplace seller)</option>
              <option value="videographer">Videographer (marketplace seller)</option>
              <option value="admin">Admin (full studio access)</option>
            </select>
          </div>

          {role === "client" && (
            <>
              <ChipPicker
                label="Linked rider names"
                options={riderOptions}
                values={linkedRiders}
                onChange={setLinkedRiders}
                placeholder="Type to search — or add a new one"
                listId="edit-user-rider-list"
                testid="edit-user-rider"
              />
              <div className="text-[10px] text-stone-500 -mt-2">Client sees any clip whose rider name matches (case-insensitive).</div>

              <ChipPicker
                label="Linked horse names"
                options={horseOptions}
                values={linkedHorses}
                onChange={setLinkedHorses}
                placeholder="Type to search — or add a new one"
                listId="edit-user-horse-list"
                testid="edit-user-horse"
              />
              <div className="text-[10px] text-stone-500 -mt-2">Or matching horse. Both filters use OR — either match shows the clip.</div>
            </>
          )}

          <ChipPicker
            label="Associations they administer"
            options={associationOptions}
            values={linkedAssociations}
            onChange={setLinkedAssociations}
            placeholder="e.g. CCA, WPRA — press Enter"
            listId="edit-user-assoc-list"
            testid="edit-user-association"
          />
          <div className="text-[10px] text-stone-500 -mt-2">Shown as "My Associations" on their account. Multiple allowed.</div>

          <button type="submit" disabled={busy} className="btn-primary w-full mt-2" data-testid="edit-user-submit">
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </form>
      </div>
    </div>
  );
}
